import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Table, Button, Input, Space, Card, Typography, Layout, Spin, Alert, Form, Col, Row, Select, Modal, message, Badge, Tooltip } from 'antd';
import { SearchOutlined, ReloadOutlined, FilterOutlined, SortAscendingOutlined, SortDescendingOutlined, SaveOutlined, DeleteOutlined, CheckCircleOutlined, CloseCircleOutlined, SyncOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
// import {baseUrl_new} from "../../api/BaseUrl";
import {API_URL} from "../../config";
import { useSidebar } from '../../context/SidebarContext';
const { Title } = Typography;
const { Content } = Layout;
const { Search } = Input;
const { Option } = Select;

const Stocks = () => {
    const { isOpen } = useSidebar();
    const navigate = useNavigate();
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [error, setError] = useState(null);
    const [sortByPresent, setSortByPresent] = useState(() => {
        const savedSort = localStorage.getItem('stocksSortByPresent');
        return savedSort ? JSON.parse(savedSort) : null;
    });
    const [filters, setFilters] = useState(() => {
        const savedFilters = localStorage.getItem('stocksFilters');
        return savedFilters ? JSON.parse(savedFilters) : {
            article: '',
            color: '',
            shortName: '',
            present: ''
        };
    });
    const [previewImage, setPreviewImage] = useState('');
    const [previewVisible, setPreviewVisible] = useState(false);
    const [modifiedItems, setModifiedItems] = useState({});
    const [originalValues, setOriginalValues] = useState({});
    const [saveResults, setSaveResults] = useState(null);
    const [successfulUpdates, setSuccessfulUpdates] = useState(new Set());
    const [filterHeight, setFilterHeight] = useState(350); // Начальная оценка высоты
    const [apiSettings, setApiSettings] = useState(null);

    const [form] = Form.useForm();

    useEffect(() => {
        const loadApiSettings = async () => {
            try {
                const token = localStorage.getItem('token');
                const response = await axios.get(`${API_URL}/v1/Settings/getOzoneApi`, {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });
                if (response.data) {
                    setApiSettings(response.data);
                }
            } catch (error) {
                console.error('Error loading API settings:', error);
                message.error('Ошибка загрузки настроек API OZON');
                setError('Для работы с остатками необходимо настроить API OZON в профиле пользователя');
            }
        };

        loadApiSettings();
    }, []);

    useEffect(() => {
        if (apiSettings) {
            fetchData(1, true, sortByPresent);
        }
    }, [apiSettings]);

    useEffect(() => {
        form.setFieldsValue({
            ...filters,
            sort_by_present: sortByPresent
        });
    }, []);

    useEffect(() => {
        localStorage.setItem('stocksFilters', JSON.stringify(filters));
    }, [filters]);

    useEffect(() => {
        localStorage.setItem('stocksSortByPresent', JSON.stringify(sortByPresent));
    }, [sortByPresent]);

    useEffect(() => {
        const updateFilterHeight = () => {
            const filterBlock = document.getElementById('filters-block');
            if (filterBlock) {
                const height = filterBlock.offsetHeight;
                setFilterHeight(height + 20); // 20px для отступа
            }
        };

        updateFilterHeight();
        window.addEventListener('resize', updateFilterHeight);
        return () => window.removeEventListener('resize', updateFilterHeight);
    }, []);

    const fetchData = async (page = 1, newSearch = false, sortOrder = sortByPresent) => {
        if (!apiSettings) {
            setError('Для работы с остатками необходимо настроить API OZON в профиле пользователя');
            return;
        }

        try {
            setLoading(true);
            setError(null);
            const params = {
                page,
                sort_by_present: sortOrder,
                sort_by_article: sortOrder === null ? 'asc' : null
            };

            if (filters.article) params.article = filters.article;
            if (filters.color) params.color = filters.color;
            if (filters.shortName) params.shortName = filters.shortName;
            if (filters.present) params.present = parseInt(filters.present);

            const token = localStorage.getItem('token');
            const response = await axios.get(`${API_URL}/v1/stocks/items`, { 
                params,
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            
            // Сохраняем оригинальные значения для новых данных
            const newOriginalValues = {};
            response.data.items.forEach(item => {
                newOriginalValues[item.article] = item.present;
            });

            if (newSearch) {
                setData(response.data.items);
                // При новом поиске полностью заменяем оригинальные значения
                setOriginalValues(newOriginalValues);
            } else {
                setData(prevData => [...prevData, ...response.data.items]);
                // При подгрузке добавляем новые оригинальные значения к существующим
                setOriginalValues(prev => ({
                    ...prev,
                    ...newOriginalValues
                }));
            }
            
            setHasMore(page < response.data.total_pages);
            setCurrentPage(page);
        } catch (error) {
            console.error('Error fetching data:', error);
            setError('Ошибка при загрузке данных. Пожалуйста, попробуйте позже.');
        } finally {
            setLoading(false);
        }
    };

    const handleFiltersSubmit = (values) => {
        const newFilters = {
            article: values.article || '',
            color: values.color || '',
            shortName: values.shortName || '',
            present: values.present || ''
        };
        
        setSortByPresent(values.sort_by_present || null);
        setFilters(newFilters);
        setCurrentPage(1);
        fetchData(1, true, values.sort_by_present);
    };

    const handleFiltersClear = () => {
        const emptyFilters = {
            article: '',
            color: '',
            shortName: '',
            present: ''
        };
        form.resetFields();
        setFilters(emptyFilters);
        setSortByPresent(null);
        setCurrentPage(1);
        fetchData(1, true, null);
        
        localStorage.removeItem('stocksFilters');
        localStorage.removeItem('stocksSortByPresent');
    };

    const handleSortByPresent = () => {
        const nextSort = sortByPresent === null ? 'asc' : 
                        sortByPresent === 'asc' ? 'desc' : null;
        setSortByPresent(nextSort);
        fetchData(1, true, nextSort);
    };

    const handleInputChange = (e, field) => {
        const value = e.target.value;
        form.setFieldValue(field, value);
        
        if (e.nativeEvent.inputType === 'insertLineBreak') {
            const formValues = form.getFieldsValue();
            handleFiltersSubmit(formValues);
        }
    };

    const loadMore = () => {
        if (!loading && hasMore) {
            fetchData(currentPage + 1);
        }
    };

    const handleRefresh = () => {
        setFilters({
            article: '',
            color: '',
            shortName: '',
            present: ''
        });
        setCurrentPage(1);
        fetchData(1, true);
    };

    const handlePreviewImage = (imageUrl) => {
        setPreviewImage(imageUrl);
        setPreviewVisible(true);
    };

    const handlePreviewClose = () => {
        setPreviewVisible(false);
    };

    const handleQuantityChange = (article, newValue) => {
        const numValue = parseInt(newValue);
        if (!isNaN(numValue) && numValue >= 0) {
            let updatedModifiedItems = { ...modifiedItems };
            updatedModifiedItems[article] = numValue;

            // Проверяем, начинается ли артикул с "HB"
            if (article.startsWith('HB')) {
                // Извлекаем цифровую часть артикула
                const numericPart = article.match(/\d+/)?.[0];
                if (numericPart) {
                    // Создаем варианты артикулов
                    const variants = [
                        `HB-PRO-${numericPart}`,
                        `HB-EXT-${numericPart}`,
                        `HB-STD-${numericPart}`
                    ];

                    // Добавляем варианты в список изменений с тем же количеством
                    variants.forEach(variant => {
                        if (variant !== article) { // Избегаем дублирования исходного артикула
                            updatedModifiedItems[variant] = numValue;
                            // Добавляем оригинальные значения для новых вариантов
                            if (!originalValues[variant]) {
                                const variantItem = data.find(item => item.article === variant);
                                setOriginalValues(prev => ({
                                    ...prev,
                                    [variant]: variantItem?.present || 0
                                }));
                            }
                        }
                    });
                }
            }

            setModifiedItems(updatedModifiedItems);
            
            setData(prevData => 
                prevData.map(item => 
                    item.article === article 
                        ? { ...item, present: numValue }
                        : item
                )
            );
        }
    };

    const getCurrentValue = (record) => {
        // Если есть модифицированное значение, возвращаем его
        if (modifiedItems[record.article] !== undefined) {
            return modifiedItems[record.article];
        }
        // Иначе возвращаем текущее значение из данных
        return record.present;
    };

    const handleSaveChanges = async () => {
        try {
            setLoading(true);
            setSaveResults(null);

            // Собираем все изменения, включая варианты HB
            const allChanges = Object.entries(modifiedItems).map(([article, present]) => {
                const changes = [{
                    article,
                    present
                }];

                // Если это HB артикул, добавляем все его варианты
                if (article.startsWith('HB')) {
                    const numericPart = article.match(/\d+/)?.[0];
                    if (numericPart) {
                        const variants = [
                            `HB-PRO-${numericPart}`,
                            `HB-EXT-${numericPart}`,
                            `HB-STD-${numericPart}`
                        ];
                        
                        variants.forEach(variant => {
                            if (variant !== article && modifiedItems[variant] !== undefined) {
                                changes.push({
                                    article: variant,
                                    present: modifiedItems[variant]
                                });
                            }
                        });
                    }
                }

                return changes;
            }).flat();

            const uniqueChanges = Array.from(
                new Map(allChanges.map(item => [item.article, item])).values()
            );

            const changesForBackend = {
                items: uniqueChanges
            };

            const token = localStorage.getItem('token');
            const response = await axios.put(`${API_URL}/v1/stocks/update-quantities`, changesForBackend, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            const results = response.data;
            setSaveResults(results);
            
            // Обновляем список успешно обновленных товаров
            const newSuccessfulUpdates = new Set(successfulUpdates);
            results.results.forEach(result => {
                if (result.updated && !result.errors?.length) {
                    newSuccessfulUpdates.add(result.offer_id);
                }
            });
            setSuccessfulUpdates(newSuccessfulUpdates);

            if (results.status === 'success') {
                message.success('Все изменения успешно сохранены');
                fetchData(currentPage, true, sortByPresent);
            } else if (results.status === 'partial_success') {
                message.warning(`Сохранено ${results.successful_count} из ${results.successful_count + results.failed_count} изменений`);
                fetchData(currentPage, true, sortByPresent);
            } else {
                message.error('Ошибка при сохранении изменений');
            }
        } catch (error) {
            console.error('Error saving changes:', error);
            message.error(error.response?.data?.error || 'Ошибка при сохранении изменений');
            setSaveResults({
                status: 'error',
                message: error.response?.data?.error || 'Ошибка при сохранении изменений'
            });
        } finally {
            setLoading(false);
        }
    };

    const handleCancelChanges = () => {
        // Восстанавливаем оригинальные значения в таблице
        setData(prevData => 
            prevData.map(item => ({
                ...item,
                present: originalValues[item.article] || item.present
            }))
        );
        // Очищаем список измененных элементов, оригинальных значений и успешных обновлений
        setModifiedItems({});
        setOriginalValues({});
        setSuccessfulUpdates(new Set());
        setSaveResults(null);
        message.info('Изменения отменены');
    };

    const handleRemoveChange = (article) => {
        const originalValue = originalValues[article];

        // Восстанавливаем оригинальное значение в таблице
        setData(prevData => 
            prevData.map(item => 
                item.article === article 
                    ? { ...item, present: originalValue }
                    : item
            )
        );

        // Удаляем из списка измененных элементов
        setModifiedItems(prev => {
            const newModifiedItems = { ...prev };
            delete newModifiedItems[article];
            return newModifiedItems;
        });

        // Находим и обновляем значение в поле ввода
        const inputElement = document.querySelector(`input[data-article="${article}"]`);
        if (inputElement) {
            inputElement.value = originalValue;
        }

        message.info(`Изменение для артикула ${article} отменено`);
    };

    const columns = [
        {
            title: 'Изображение',
            dataIndex: 'primary_image',
            key: 'primary_image',
            width: 80,
            render: (text) => text ? (
                <img 
                    src={text} 
                    alt="product" 
                    style={{ 
                        width: '40px',
                        height: '40px',
                        objectFit: 'contain',
                        borderRadius: '4px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        cursor: 'pointer'
                    }}
                    onClick={() => handlePreviewImage(text)}
                />
            ) : 'Нет изображения'
        },
        {
            title: (
                <Space>
                    Артикул
                    <SortAscendingOutlined style={{ color: '#1890ff' }} />
                </Space>
            ),
            dataIndex: 'article',
            key: 'article',
            width: 120,
            defaultSortOrder: 'ascend',
            filterDropdown: ({ setSelectedKeys, selectedKeys, confirm }) => (
                <div style={{ padding: 8 }}>
                    <Input
                        placeholder="Поиск по артикулу"
                        value={selectedKeys[0]}
                        onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                        onPressEnter={(event) => handleInputChange(event, 'article')}
                        style={{ width: 188, marginBottom: 8, display: 'block' }}
                    />
                    <Space>
                        <Button
                            type="primary"
                            onClick={(event) => handleInputChange({ target: { value: selectedKeys[0] }, nativeEvent: { inputType: 'insertLineBreak' } }, 'article')}
                            icon={<SearchOutlined />}
                            size="small"
                        >
                            Поиск
                        </Button>
                        <Button
                            onClick={() => {
                                setSelectedKeys([]);
                                handleInputChange({ target: { value: '' }, nativeEvent: { inputType: 'insertLineBreak' } }, 'article');
                            }}
                            size="small"
                        >
                            Сброс
                        </Button>
                    </Space>
                </div>
            ),
            filterIcon: filtered => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
        },
        {
            title: 'Наименование',
            dataIndex: 'shortName',
            key: 'shortName',
            width: '40%',
            ellipsis: true,
            filterDropdown: ({ setSelectedKeys, selectedKeys, confirm }) => (
                <div style={{ padding: 8 }}>
                    <Input
                        placeholder="Поиск по наименованию"
                        value={selectedKeys[0]}
                        onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                        onPressEnter={(event) => handleInputChange(event, 'shortName')}
                        style={{ width: 188, marginBottom: 8, display: 'block' }}
                    />
                    <Space>
                        <Button
                            type="primary"
                            onClick={(event) => handleInputChange({ target: { value: selectedKeys[0] }, nativeEvent: { inputType: 'insertLineBreak' } }, 'shortName')}
                            icon={<SearchOutlined />}
                            size="small"
                        >
                            Поиск
                        </Button>
                        <Button
                            onClick={() => {
                                setSelectedKeys([]);
                                handleInputChange({ target: { value: '' }, nativeEvent: { inputType: 'insertLineBreak' } }, 'shortName');
                            }}
                            size="small"
                        >
                            Сброс
                        </Button>
                    </Space>
                </div>
            ),
            filterIcon: filtered => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
        },
        {
            title: 'Цвет',
            dataIndex: 'Color',
            key: 'color',
            width: '30%',
            ellipsis: true,
            filterDropdown: ({ setSelectedKeys, selectedKeys, confirm }) => (
                <div style={{ padding: 8 }}>
                    <Input
                        placeholder="Поиск по цвету"
                        value={selectedKeys[0]}
                        onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                        onPressEnter={(event) => handleInputChange(event, 'color')}
                        style={{ width: 188, marginBottom: 8, display: 'block' }}
                    />
                    <Space>
                        <Button
                            type="primary"
                            onClick={(event) => handleInputChange({ target: { value: selectedKeys[0] }, nativeEvent: { inputType: 'insertLineBreak' } }, 'color')}
                            icon={<SearchOutlined />}
                            size="small"
                        >
                            Поиск
                        </Button>
                        <Button
                            onClick={() => {
                                setSelectedKeys([]);
                                handleInputChange({ target: { value: '' }, nativeEvent: { inputType: 'insertLineBreak' } }, 'color');
                            }}
                            size="small"
                        >
                            Сброс
                        </Button>
                    </Space>
                </div>
            ),
            filterIcon: filtered => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
        },
        {
            title: (
                <Space>
                    Наличие
                    <Button
                        type="text"
                        icon={sortByPresent === 'asc' ? <SortAscendingOutlined /> : 
                              sortByPresent === 'desc' ? <SortDescendingOutlined /> : 
                              <SortAscendingOutlined style={{ color: '#d9d9d9' }} />}
                        onClick={handleSortByPresent}
                        size="small"
                    />
                </Space>
            ),
            dataIndex: 'present',
            key: 'present',
            width: 100,
            render: (text, record) => (
                <Input
                    type="number"
                    value={getCurrentValue(record)}
                    min={0}
                    size="small"
                    onChange={(e) => handleQuantityChange(record.article, e.target.value)}
                    style={{
                        backgroundColor: modifiedItems[record.article] !== undefined ? '#f6ffed' : 'white',
                        borderColor: modifiedItems[record.article] !== undefined ? '#b7eb8f' : '#d9d9d9',
                        width: '100%'
                    }}
                    data-article={record.article}
                />
            )
        }
    ];

    const quickFilters = [
        { name: 'Баффы', value: 'BN-' },
        { name: 'Хобы', value: 'HB-PRO' },
        { name: 'SUM', value: 'BN-SUM' },
        { name: 'PRO', value: 'BN-PRO' },
        { name: 'ZIM', value: 'BN-ZIM' },
        { name: 'NEO', value: 'BN-NEO' },
        { name: 'JS', value: 'BN-JS' },
        { name: 'BS', value: 'BN-BS' }

    ];

    const handleQuickFilter = (filterValue) => {
        form.setFieldValue('article', filterValue);
        const formValues = form.getFieldsValue();
        formValues.article = filterValue;
        handleFiltersSubmit(formValues);
    };

    useEffect(() => {
        fetchData(1, true, sortByPresent);
    }, []);

    return (
        <Layout style={{ 
            background: '#f0f2f5', 
            minHeight: '100vh',
            width: '100%',
            position: 'relative'
        }}>
            <div 
                id="filters-block"
                style={{ 
                    position: 'fixed',
                    top: 0,
                    left: isOpen ? '16rem' : '3.5rem',
                    right: 0,
                    zIndex: 100,
                    padding: '20px',
                    background: '#f0f2f5',
                    transition: 'left 0.3s ease-in-out'
                }}
            >
                <Card style={{ 
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    marginBottom: 0
                }}>
                    <Space direction="vertical" size="large" style={{ width: '100%' }}>
                        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                            <Title level={3} style={{ margin: 0 }}>Остатки товаров</Title>
                            <Space>
                            <Button
                                icon={<SyncOutlined />}
                                onClick={() => navigate('/ozon/sync')}
                            >
                                Синхронизация Ozon
                            </Button>
                            <Button 
                                icon={<ReloadOutlined />} 
                                onClick={handleRefresh}
                                loading={loading}
                            >
                                Обновить
                            </Button>
                            </Space>
                        </Space>

                        <Form
                            form={form}
                            onFinish={handleFiltersSubmit}
                            layout="vertical"
                            style={{ width: '100%' }}
                            initialValues={{
                                ...filters,
                                sort_by_present: sortByPresent
                            }}
                        >
                            <Row gutter={[16, 16]}>
                                <Col xs={24} sm={8}>
                                    <Form.Item name="article" label="Артикул">
                                        <Input
                                            placeholder="Поиск по артикулу"
                                            prefix={<SearchOutlined />}
                                            allowClear
                                            onChange={(e) => handleInputChange(e, 'article')}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={8}>
                                    <Form.Item name="shortName" label="Наименование">
                                        <Input
                                            placeholder="Поиск по наименованию"
                                            prefix={<SearchOutlined />}
                                            allowClear
                                            onChange={(e) => handleInputChange(e, 'shortName')}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={8}>
                                    <Form.Item name="color" label="Цвет">
                                        <Input
                                            placeholder="Поиск по цвету"
                                            prefix={<SearchOutlined />}
                                            allowClear
                                            onChange={(e) => handleInputChange(e, 'color')}
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row gutter={[16, 16]}>
                                <Col xs={24} sm={8}>
                                    <Form.Item name="present" label="Количество">
                                        <Input
                                            placeholder="Поиск по количеству"
                                            type="number"
                                            allowClear
                                            onChange={(e) => handleInputChange(e, 'present')}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={8}>
                                    <Form.Item name="sort_by_present" label="Сортировка по количеству">
                                        <Select
                                            placeholder="Выберите порядок сортировки"
                                            allowClear
                                        >
                                            <Option value="asc">По возрастанию</Option>
                                            <Option value="desc">По убыванию</Option>
                                        </Select>
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row justify="end" gutter={[16, 16]}>
                                <Col>
                                    <Space>
                                        <Button onClick={handleFiltersClear}>
                                            Сбросить
                                        </Button>
                                        <Button type="primary" htmlType="submit" icon={<FilterOutlined />}>
                                            Применить фильтры
                                        </Button>
                                    </Space>
                                </Col>
                            </Row>
                        </Form>

                        <Card size="small" style={{ marginTop: '8px' }}>
                            <Space wrap>
                                {quickFilters.map((filter, index) => (
                                    <Button
                                        key={index}
                                        size="small"
                                        onClick={() => handleQuickFilter(filter.value)}
                                        type={filters.article === filter.value ? 'primary' : 'default'}
                                    >
                                        {filter.name}
                                    </Button>
                                ))}
                            </Space>
                        </Card>
                    </Space>
                </Card>
            </div>

            <Content style={{ 
                position: 'fixed',
                top: `${filterHeight}px`,
                left: isOpen ? '16rem' : '3.5rem',
                right: 0,
                bottom: 0,
                padding: '20px',
                transition: 'all 0.3s ease-in-out',
                overflowY: 'auto'
            }}>
                <Card 
                    bodyStyle={{ 
                        padding: '12px'
                    }} 
                >
                    {error && (
                        <Alert
                            message="Ошибка"
                            description={error}
                            type="error"
                            showIcon
                            closable
                            onClose={() => setError(null)}
                            style={{ marginBottom: '12px' }}
                        />
                    )}

                    <div style={{ width: '100%' }}>
                    <Table 
                        columns={columns} 
                        dataSource={data}
                        rowKey="article"
                        loading={loading}
                        pagination={false}
                        size="small"
                            style={{ width: '100%' }}
                        rowClassName={(record) => 
                            modifiedItems[record.article] !== undefined ? 'modified-row' : ''
                        }
                    />
                    </div>
                    
                    {hasMore && (
                        <div style={{ textAlign: 'center', marginBottom: '12px' }}>
                            <Button 
                                onClick={loadMore} 
                                loading={loading}
                                type="primary"
                                size="small"
                            >
                                Подгрузить еще
                            </Button>
                        </div>
                    )}

                    {Object.keys(modifiedItems).length > 0 && (
                        <Card 
                            title={
                                <Space>
                                    <span>Измененные позиции</span>
                                    <Badge 
                                        count={Object.keys(modifiedItems).length} 
                                        style={{ 
                                            backgroundColor: '#1890ff',
                                            marginLeft: '8px'
                                        }} 
                                    />
                                </Space>
                            } 
                            size="small"
                        >
                            <Space direction="vertical" style={{ width: '100%' }}>
                                <div style={{ width: '100%' }}>
                                    {Object.entries(modifiedItems).map(([article, newValue]) => {
                                        const oldValue = originalValues[article];
                                        const item = data.find(item => item.article === article);
                                        const saveResult = saveResults?.results?.find(r => r.offer_id === article);
                                        const isSuccessful = successfulUpdates.has(article);
                                        
                                        return (
                                            <div key={article} style={{ 
                                                padding: '8px',
                                                background: isSuccessful ? '#f6ffed' : '#fafafa',
                                                borderRadius: '4px',
                                                marginBottom: '8px',
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                border: isSuccessful ? '1px solid #b7eb8f' : '1px solid transparent'
                                            }}>
                                                <Space size="middle" style={{ flex: 1 }}>
                                                    {(saveResult || isSuccessful) && (
                                                        <Badge 
                                                            count={
                                                                (saveResult?.updated && !saveResult?.errors?.length) || isSuccessful ? 
                                                                    <CheckCircleOutlined style={{ color: '#52c41a' }} /> : 
                                                                    <CloseCircleOutlined style={{ color: '#ff4d4f' }} />
                                                            } 
                                                        />
                                                    )}
                                                    <Space size={4}>
                                                        <strong>Артикул:</strong>
                                                        <span>{article}</span>
                                                    </Space>
                                                    <span style={{ 
                                                        color: '#1890ff',
                                                        maxWidth: '300px',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap'
                                                    }}>
                                                        {item?.shortName || 'Без названия'}
                                                    </span>
                                                    <Space size={4}>
                                                        <strong>Старое количество:</strong>
                                                        <span style={{ color: '#ff4d4f' }}>{oldValue}</span>
                                                    </Space>
                                                    <Space size={4}>
                                                        <strong>Новое количество:</strong>
                                                        <span style={{ color: '#52c41a' }}>{newValue}</span>
                                                    </Space>
                                                    <span style={{ 
                                                        color: newValue > oldValue ? '#52c41a' : 
                                                               newValue < oldValue ? '#ff4d4f' : 
                                                               '#8c8c8c'
                                                    }}>
                                                        ({newValue > oldValue ? '+' : ''}{newValue - oldValue})
                                                    </span>
                                                    {saveResult?.errors?.length > 0 && (
                                                        <Tooltip title={saveResult.errors.join('\n')}>
                                                            <span style={{ color: '#ff4d4f', cursor: 'help' }}>
                                                                Ошибки: {saveResult.errors.length}
                                                            </span>
                                                        </Tooltip>
                                                    )}
                                                    {isSuccessful && !saveResult?.errors?.length && (
                                                        <span style={{ color: '#52c41a' }}>
                                                            Сохранено
                                                        </span>
                                                    )}
                                                </Space>
                                                <Button 
                                                    type="text" 
                                                    danger
                                                    icon={<DeleteOutlined />}
                                                    onClick={() => handleRemoveChange(article)}
                                                    disabled={loading}
                                                />
                                            </div>
                                        );
                                    })}
                                </div>
                                {saveResults && (
                                    <Alert
                                        message={
                                            <Space direction="vertical">
                                                <div>
                                                    Статус сохранения: {
                                                        saveResults.status === 'success' ? 'Успешно' :
                                                        saveResults.status === 'partial_success' ? 'Частично успешно' :
                                                        'Ошибка'
                                                    }
                                                </div>
                                                <div>
                                                    Успешно обновлено: {saveResults.successful_count} товаров
                                                </div>
                                                {saveResults.failed_count > 0 && (
                                                    <div style={{ color: '#ff4d4f' }}>
                                                        Не удалось обновить: {saveResults.failed_count} товаров
                                                    </div>
                                                )}
                                            </Space>
                                        }
                                        type={
                                            saveResults.status === 'success' ? 'success' :
                                            saveResults.status === 'partial_success' ? 'warning' :
                                            'error'
                                        }
                                        showIcon
                                        style={{ marginTop: '16px' }}
                                    />
                                )}
                                <Space style={{ marginTop: '16px' }}>
                                    <Button
                                        type="primary"
                                        icon={<SaveOutlined />}
                                        onClick={handleSaveChanges}
                                        loading={loading}
                                    >
                                        Отправить в OZON ({Object.keys(modifiedItems).length})
                                    </Button>
                                    <Button
                                        danger
                                        onClick={handleCancelChanges}
                                        disabled={loading}
                                    >
                                        Очистить
                                    </Button>
                                </Space>
                            </Space>
                        </Card>
                    )}
                </Card>
                <Modal
                    visible={previewVisible}
                    footer={null}
                    onCancel={handlePreviewClose}
                    width={800}
                    centered
                >
                    <img
                        alt="preview"
                        style={{ width: '100%' }}
                        src={previewImage}
                    />
                </Modal>
            </Content>

            <style jsx global>{`
                .ant-layout {
                    transition: all 0.3s ease-in-out;
                }
                .ant-table-wrapper {
                    width: 100% !important;
                }
                .ant-table {
                    width: 100% !important;
                }
                .ant-table-container {
                    width: 100% !important;
                }
                .ant-table-content {
                    width: 100% !important;
                }
                .ant-card {
                    width: 100% !important;
                }
                .ant-card-body {
                    width: 100% !important;
                    padding: 12px !important;
                }
                .modified-row {
                    background-color: #f6ffed;
                }
                .modified-row:hover > td {
                    background-color: #e6f7ff !important;
                }
            `}</style>
        </Layout>
    );
};

export default Stocks;